import React, { Component } from 'react';
import sortBy from 'lodash/sortBy';
import union from 'lodash/union';
import { scaleLinear } from 'd3-scale';

import Dimensions from '../dimensions/dimensions';
import Map from '../map/zoom-test3.js';
import MapCounter from '../map-counter/map-counter';
import MapLegend, { MapLegendScale } from '../map-legend/map-legend';
import MiniMapLegend from '../MiniMapLegend';
import MapActions from '../../components/MapActions';
import { DownloadRequest } from '../../utilities/downloadRequest.js';
import DownloadMenu from '../../components/download-menu/download-menu.jsx';
import ShareButton from '../../components/share-button/share-button.jsx';
import {
  MapWrapper,
  MapWrapperMain,
  MapWrapperFooter,
  MapWrapperHeader,
} from '../../components/MapWrapper';
import Loader from '../../components/loader/loader.jsx';
import { TradeQueryReadout } from '../../components/QueryReadout';

import formatters from '../../formatters';
import { nice } from '../../helpers';
import { filterZoomGroupsExcludedCountries } from './utils.js';
import { exportMapImage, exportMapSvg } from '../../utilities/exportMapImage.js';

export default class MapContainer extends Component {
  constructor(props) {
    super();

    this.state = {
      data: null,
      flowScale: null,
      countries: null,
      imageStatus: 'uninitialized',
      downloadStatus: 'uninitialized',
    };

    this.prepareData(props.data, props.query);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.data !== nextProps.data || this.props.query !== nextProps.query) {
      this.prepareData(nextProps.data, nextProps.query);
    }
  }

  // XXX: Setting state from props is an antipattern in React, but if we process data in render
  // we have to do it even if only hovered flow/country has changed (in which case we don't want
  // to rerender defs) but we then end up with a different object and not compare to previous
  // props in shouldComponentUpdate
  prepareData(rawData, query) {
    if (!rawData || !rawData.length) {
      this.setState({
        data: null,
        flowScale: null,
        countries: null,
      });

      return;
    }

    let data = [];

    rawData.forEach(function (datum) {
      const value = datum[query.units];

      // Skip flows with no data, which will be where there is value but no weight data
      // e.g Albania → Belgium, Cement, 2015
      // FIXME: Would be better to prefilter the returned data and fall through to existing
      // no data handling
      if (value === null) {
        return;
      }

      data.push({
        exporter: datum.exporter,
        importer: datum.importer,
        value: value,
        original: datum,
      });
    });

    data = sortBy(data, ['value']).reverse();

    let max = data.length ? data[0].value : null;
    max = nice(max);

    const flowScale = scaleLinear().domain([0, max]).range([0, 12]);

    // Filter out small flows based on thickness scale
    data = data.filter(function (datum, i) {
      return i < 5 || flowScale(datum.value) >= 0.9;
    });

    const countries = data.reduce(function (memo, datum) {
      if (memo.indexOf(datum.exporter) < 0) {
        memo.push(datum.exporter);
      }

      if (memo.indexOf(datum.importer) < 0) {
        memo.push(datum.importer);
      }

      return memo;
    }, []);

    console.log('>', query.exporter, query.importer);

    let zoomCountries = query.exporter.id || query.importer.id ? [...countries] : [];
    // FIXME: Zoom group excluded countries are not excluded if they are already in the above countries array
    if (query.exporter.children && query.exporter.children.length) {
      zoomCountries = union(
        zoomCountries,
        filterZoomGroupsExcludedCountries(query.exporter.id, query.exporter.children)
      );
    }
    if (query.importer.children && query.importer.children.length) {
      zoomCountries = union(
        zoomCountries,
        filterZoomGroupsExcludedCountries(query.importer.id, query.importer.children)
      );
    }
    console.log('countries', countries, zoomCountries);

    this.setState({
      data: data,
      flowScale: flowScale,
      countries: countries,
      zoomCountries: zoomCountries,
    });
  }

  buildImageFilename(extension = 'png') {
    const query = this.props.query;
    const parts = ['resourcetransitions-trade'];

    ['exporter', 'importer', 'category', 'year'].forEach(function (key) {
      parts.push(query[key] && query[key].id ? query[key].id : 'all');
    });

    return `${parts.join('-')}.${extension}`;
  }

  // The map, laid out at whatever size its container provides. Shared between the
  // on-screen render and the off-screen export so both stay in sync.
  renderMap() {
    const { data, flowScale, countries, zoomCountries } = this.state;

    return (
      // Below tablet breakpoint force the map to scale down rather than relayout
      // FIXME: Get dimensions from breakpoints or CSS?
      <Dimensions minWidth={748} minHeight={343}>
        <Map
          {...this.props}
          data={data}
          flowScale={flowScale}
          countries={countries}
          zoomCountries={zoomCountries}
        />
      </Dimensions>
    );
  }

  // Save button: the full image, including the readout/legend/credit furniture.
  handleSaveImage = () => this.exportImage({ furniture: true });

  // CMS-only: the bare map, with no readout/legend/credit.
  handleSavePlainImage = () => this.exportImage({ furniture: false });

  // CMS-only: the bare map as a vector SVG.
  handleSavePlainSvg = () => {
    if (!this.state.data || !this.state.data.length || this.state.imageStatus === 'pending') {
      return;
    }

    this.setState({ imageStatus: 'pending' });

    exportMapSvg({
      mapVNode: this.renderMap(),
      svgSelector: '.map__svg',
      filename: this.buildImageFilename('svg'),
    }).finally(() => this.setState({ imageStatus: 'uninitialized' }));
  };

  // Data (CSV) download: kick off the async export job, navigating to the file
  // once it's ready. `downloadStatus` drives the menu's pending state meanwhile.
  handleDownloadData = () => {
    if (this.state.downloadStatus === 'pending') {
      return;
    }

    this.setState({ downloadStatus: 'pending' });

    new DownloadRequest(this.props.query, () => {
      this.setState({ downloadStatus: 'uninitialized' });
    });
  };

  // Export the map as a PNG. When `furniture` is false the download is just the
  // bare map, with no readout, legend or credit (for a bare-map trigger to be
  // wired up separately).
  exportImage = ({ furniture = true } = {}) => {
    if (!this.state.data || !this.state.data.length || this.state.imageStatus === 'pending') {
      return;
    }

    this.setState({ imageStatus: 'pending' });

    const formatter = this.props.query.units === 'value' ? formatters.currency : formatters.weight;

    exportMapImage({
      mapVNode: this.renderMap(),
      svgSelector: '.map__svg',
      filename: this.buildImageFilename(),
      readoutVNode: <TradeQueryReadout query={this.props.query} />,
      // The scale-bar <svg> on its own — no "Scale" heading — so it rasterises directly.
      legendVNode: this.state.flowScale ? (
        <MapLegendScale scale={this.state.flowScale} formatter={formatter} />
      ) : null,
      furniture,
    }).finally(() => this.setState({ imageStatus: 'uninitialized' }));
  };

  render(props, state) {
    let children = null;

    if (state.data && state.data.length) {
      children = this.renderMap();
    }

    const formatter = props.query.units === 'value' ? formatters.currency : formatters.weight;

    return (
      <MapWrapper modifier={props.kind}>
        {props.header && <MapWrapperHeader modifier="trade">{props.header}</MapWrapperHeader>}
        <MapWrapperMain>
          <Loader visible={props.loading} transparent={true} />
          {children}
        </MapWrapperMain>
        <MapWrapperFooter modifier="trade">
          {state.flowScale ? <MapLegend scale={state.flowScale} formatter={formatter} /> : null}
          {/* Don't show mini map legend on embeds */}
          {!props.kind && <MiniMapLegend />}
          {!props.kind && (
            <MapActions
              status={
                state.data && state.data.length ? (
                  <MapCounter count={state.data.length} total={props.total} />
                ) : null
              }
            >
              <ShareButton queryString={props.query.toString()} message={__config.twitterMessage} />
              <DownloadMenu
                imageStatus={state.imageStatus}
                onDownloadImage={this.handleSaveImage}
                onDownloadPlainImage={
                  __config.allowPlainImageDownload ? this.handleSavePlainImage : null
                }
                onDownloadPlainSvg={
                  __config.allowPlainImageDownload ? this.handleSavePlainSvg : null
                }
                dataStatus={state.downloadStatus}
                onDownloadData={this.handleDownloadData}
              />
            </MapActions>
          )}
        </MapWrapperFooter>
      </MapWrapper>
    );
  }
}
